import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { CheckCircle2, AlertCircle, Info, X, AlertTriangle } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  action?: ToastAction;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  success: (title: string, message?: string, action?: ToastAction) => void;
  error: (title: string, message?: string, action?: ToastAction) => void;
  info: (title: string, message?: string, action?: ToastAction) => void;
  warning: (title: string, message?: string, action?: ToastAction) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = Math.random().toString(36).substring(2, 9);
      const newToast = { ...toast, id };
      setToasts((prev) => [...prev, newToast]);

      // Auto-remove after duration (longer if there's an action)
      const defaultDuration = toast.action ? 6000 : toast.type === 'error' ? 5000 : 3000;
      const duration = toast.duration ?? defaultDuration;
      setTimeout(() => removeToast(id), duration);
    },
    [removeToast]
  );

  const success = useCallback(
    (title: string, message?: string, action?: ToastAction) => addToast({ type: 'success', title, message, action }),
    [addToast]
  );

  const error = useCallback(
    (title: string, message?: string, action?: ToastAction) => addToast({ type: 'error', title, message, action, duration: 5000 }),
    [addToast]
  );

  const info = useCallback(
    (title: string, message?: string, action?: ToastAction) => addToast({ type: 'info', title, message, action }),
    [addToast]
  );

  const warning = useCallback(
    (title: string, message?: string, action?: ToastAction) => addToast({ type: 'warning', title, message, action }),
    [addToast]
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, info, warning }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, removeToast }: { toasts: Toast[]; removeToast: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-3 pointer-events-none [&>*]:pointer-events-auto"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((toast, index) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onClose={() => removeToast(toast.id)}
          index={index}
        />
      ))}
    </div>
  );
}

function ToastItem({ toast, onClose, index }: { toast: Toast; onClose: () => void; index: number }) {
  const config = {
    success: {
      icon: CheckCircle2,
      bgClass: 'bg-green-500/10 border-green-500/30',
      iconClass: 'text-green-400',
      titleClass: 'text-green-400',
      glowClass: 'shadow-green-500/10',
    },
    error: {
      icon: AlertCircle,
      bgClass: 'bg-red-500/10 border-red-500/30',
      iconClass: 'text-red-400',
      titleClass: 'text-red-400',
      glowClass: 'shadow-red-500/10',
    },
    warning: {
      icon: AlertTriangle,
      bgClass: 'bg-yellow-500/10 border-yellow-500/30',
      iconClass: 'text-yellow-400',
      titleClass: 'text-yellow-400',
      glowClass: 'shadow-yellow-500/10',
    },
    info: {
      icon: Info,
      bgClass: 'bg-blue-500/10 border-blue-500/30',
      iconClass: 'text-blue-400',
      titleClass: 'text-blue-400',
      glowClass: 'shadow-blue-500/10',
    },
  };

  const { icon: Icon, bgClass, iconClass, titleClass, glowClass } = config[toast.type];

  function handleActionClick() {
    toast.action?.onClick();
    onClose();
  }

  return (
    <div
      role={toast.type === 'error' ? 'alert' : 'status'}
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      className={`flex items-start gap-3 rounded-xl border ${bgClass} bg-cacao-dark-secondary/95 backdrop-blur-sm p-4 shadow-xl ${glowClass}`}
      style={{
        minWidth: '320px',
        maxWidth: '420px',
        animation: `slide-in-from-right 0.3s ease-out ${index * 0.05}s both`,
      }}
    >
      <div className={`flex-shrink-0 p-1 rounded-full ${bgClass}`}>
        <Icon className={`h-4 w-4 ${iconClass}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-medium text-sm ${titleClass}`}>{toast.title}</p>
        {toast.message && (
          <p className="mt-0.5 text-sm text-cacao-muted leading-relaxed">{toast.message}</p>
        )}
        {toast.action && (
          <button
            onClick={handleActionClick}
            className={`mt-2 text-sm font-medium ${titleClass} hover:underline transition-all`}
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        onClick={onClose}
        className="flex-shrink-0 rounded-lg p-1.5 text-cacao-muted hover:bg-cacao-dark-tertiary hover:text-white transition-all"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
